import React, {
  useEffect,
  useMemo,
  useState,
  HTMLAttributes,
  ReactElement,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./map-left-panel.module.scss";
import chevron from "../../images/chevron-left.svg";
import CityCardBig from "../../components/features/cities/city-cards/city-card-big/city-card-big";
import { clearOpenedCity, setOpenedCity } from "../../services/reducers/cities";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { handleSelectCard } from "../../utils/cardNavigation";
import OpenedFilterCard from "../../components/features/filters/opened-filter-card/opened-filter-card";
import ProductsCardBig from "../../components/features/products/products-card-big/products-card-big";
import { selectProductsDataByName } from "../../services/reducers/products";
import {
  closeSelectedCard,
  setCurrentOpenedCardPage,
} from "../../services/reducers/filters";

export interface MapLeftPanelProps extends HTMLAttributes<HTMLDivElement> {
  leftPanelOpened: boolean;
  closeLeftPanel: (closeLeftPanel: boolean) => void;
  onWidthChange?: (width: number) => void;
  isExtendedWidth?: boolean;
}

export function MapLeftPanel({
  className,
  children,
  leftPanelOpened,
  closeLeftPanel,
  onWidthChange,
  isExtendedWidth = false,
  ...props
}: MapLeftPanelProps): ReactElement {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const citiesData = useAppSelector((store) => store.cities.data);
  const { openedCity } = useAppSelector((store) => store.cities);
  const landPlotsData = useAppSelector((state) => state.filters.plots.data);
  const investmentOpportunitiesData = useAppSelector(
    (state) => state.filters.investmentOpportunities.data,
  );
  const spaceForRentData = useAppSelector(
    (state) => state.filters.spacesForRent.data,
  );
  const {
    currentOpenedCardPage,
    selectedCard,
    selectedCardType,
    openedFromMap,
  } = useAppSelector((state) => state.filters);
  const [selectedProductCard, setSelectedProductCard] = useState<string | null>(
    null,
  );
  const panelRef = useRef<HTMLDivElement>(null);

  const cities = searchParams.get("eid");
  const fid = searchParams.get("fid");

  const showToggleButton = useMemo(() => {
    const hasCities = cities && parseInt(cities) > 0;
    const hasFid = !!fid;
    return !leftPanelOpened || !(hasCities || hasFid);
  }, [cities, fid, leftPanelOpened]);

  useEffect(() => {
    const eid = searchParams.get("eid");
    if (eid && (!openedCity || openedCity.estateEn.toString() !== eid)) {
      const city = citiesData.find((c) => c.estateEn.toString() === eid);
      if (city) {
        dispatch(setOpenedCity(city));
        dispatch(setCurrentOpenedCardPage("cities"));
      }
    }
    if (!eid && openedCity) {
      dispatch(clearOpenedCity());
      dispatch(setCurrentOpenedCardPage("filter"));
    }
  }, [searchParams, dispatch, citiesData]);

  useEffect(() => {
    const fid = searchParams.get("fid");
    if (fid && (!selectedCard || selectedCard.fid.toString() !== fid)) {
      const plot = landPlotsData.find((plot) => plot.fid.toString() === fid);
      const space = spaceForRentData.find(
        (space) => space.fid.toString() === fid,
      );
      const opportunity = investmentOpportunitiesData.find(
        (opportunity) => opportunity.fid.toString() === fid,
      );
      if (plot) {
        handleSelectCard(dispatch, "Plots", fid, openedFromMap);
      } else if (space) {
        handleSelectCard(dispatch, "SpacesForRent", fid, openedFromMap);
      } else if (opportunity) {
        handleSelectCard(
          dispatch,
          "InvestmentOpportunities",
          fid,
          openedFromMap,
        );
      }
    }
    if (!fid && selectedCard) {
      dispatch(closeSelectedCard());
      dispatch(setCurrentOpenedCardPage("cities"));
    }
  }, [
    searchParams,
    dispatch,
    landPlotsData,
    spaceForRentData,
    investmentOpportunitiesData,
    openedFromMap,
  ]);

  const productsData = useAppSelector((state) =>
    selectProductsDataByName(state, selectedProductCard),
  );

  useEffect(() => {
    const productNameParam = searchParams.get("productName");
    if (productNameParam) {
      setSelectedProductCard(productNameParam);
    } else {
      setSelectedProductCard(null);
    }
  }, [searchParams]);

  useEffect(() => {
    function handleResize() {
      if (panelRef.current && onWidthChange) {
        onWidthChange(panelRef.current.offsetWidth);
      }
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [onWidthChange]);

  return (
    <div
      className={`${styles.mapLeftPanel} ${leftPanelOpened ? styles.panelOpened : styles.panelClosed} ${className || ""}`}
      style={isExtendedWidth ? { width: "652px" } : undefined}
      ref={panelRef}
      {...props}
    >
      {showToggleButton && (
        <button
          onClick={() => closeLeftPanel(!leftPanelOpened)}
          className={styles.toggleButton}
        >
          <img
            src={chevron}
            alt={t("map-left-panel.toggleButton.alt")}
            className={!leftPanelOpened ? styles.chevronOpened : ""}
          />
        </button>
      )}
      {children}
      {leftPanelOpened && currentOpenedCardPage === "cities" && openedCity && (
        <CityCardBig closeLeftPanel={closeLeftPanel} city={openedCity} />
      )}
      {leftPanelOpened &&
        currentOpenedCardPage === "filter" &&
        selectedCard &&
        selectedCardType && (
          <OpenedFilterCard
            type={selectedCardType}
            closeLeftPanel={closeLeftPanel}
            card={selectedCard}
          />
        )}
      {leftPanelOpened &&
        selectedProductCard &&
        productsData &&
        productsData.filter((city) => city.companies.length > 0).length && (
          <ProductsCardBig
            closeLeftPanel={closeLeftPanel}
            name={selectedProductCard}
            data={productsData}
          />
        )}
    </div>
  );
}
